import { useEffect, useRef, useState } from "react";
import { CheckCircle } from "lucide-react";
import { createPortal } from "react-dom";

// ─────────────────────────────────────────────
// MultiStepLoader
//
// Full-screen overlay that shows a list of steps being "completed"
// one by one while a long async operation runs.
// Inspired by ui.aceternity.com/components/multi-step-loader
// Used by: MitigationTab for AI recommendation generation
// ─────────────────────────────────────────────

interface MultiStepLoaderProps {
  isLoading: boolean;
  steps: string[];
  /** Milliseconds per step. Defaults to 3500. */
  stepDuration?: number;
}

/** Spinner shown next to the currently-active step */
function Spinner() {
  return (
    <span
      className="h-5 w-5 flex-shrink-0 animate-spin rounded-full border-2"
      style={{
        borderColor: "var(--accent) transparent var(--accent) transparent",
      }}
      aria-hidden
    />
  );
}

export function MultiStepLoader({
  isLoading,
  steps,
  stepDuration = 3500,
}: MultiStepLoaderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // When loading starts, reset progress and begin advancing through steps
  useEffect(() => {
    if (isLoading) {
      setCurrentIndex(0);
      setVisible(true);

      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          const next = prev + 1;
          // Keep cycling through steps — never finish before the mutation does
          return next < steps.length ? next : prev;
        });
      }, stepDuration);
    } else {
      // Loading finished — clear timer and wait one tick so the overlay
      // can fade out gracefully before unmounting
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Small delay so the last completed state is visible briefly
      const fadeTimer = setTimeout(() => setVisible(false), 400);
      return () => clearTimeout(fadeTimer);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, steps.length, stepDuration]);

  if (!visible) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Generating AI recommendations"
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.72)",
        // Fade in on mount
        animation: "fade-in 0.2s ease-out",
      }}
    >
      <div
        className="relative w-full max-w-md rounded-card border border-border-default p-8 shadow-2xl"
        style={{ backgroundColor: "var(--bg-card)" }}
      >
        {/* Header */}
        <div className="mb-6 flex flex-col gap-1">
          <h2 className="text-[18px] font-semibold text-text-primary">
            Analysing Assessment
          </h2>
          <p className="text-[13px] text-text-muted">
            Our AI is working through your responses. This may take a moment.
          </p>
        </div>

        {/* Steps list */}
        <div className="flex flex-col gap-3">
          {steps.map((step, index) => {
            const isDone = index < currentIndex;
            const isActive = index === currentIndex;
            const isPending = index > currentIndex;

            return (
              <div
                key={index}
                className="flex items-center gap-3"
                style={{
                  transition: "opacity 0.4s ease, transform 0.4s ease",
                  opacity: isPending ? 0.35 : 1,
                  transform: isActive ? "translateX(4px)" : "translateX(0)",
                }}
              >
                {/* Icon column */}
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                  {isDone ? (
                    <CheckCircle
                      className="h-5 w-5"
                      style={{ color: "var(--success)" }}
                      aria-hidden
                    />
                  ) : isActive ? (
                    <Spinner />
                  ) : (
                    // Placeholder dot for pending steps
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: "var(--border-default)" }}
                      aria-hidden
                    />
                  )}
                </span>

                {/* Label */}
                <span
                  className="text-[14px] leading-snug"
                  style={{
                    color: isDone
                      ? "var(--text-secondary)"
                      : isActive
                        ? "var(--text-primary)"
                        : "var(--text-muted)",
                    fontWeight: isActive ? 500 : 400,
                    transition: "color 0.3s ease, font-weight 0.3s ease",
                  }}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div
          className="mt-6 h-1 w-full overflow-hidden rounded-pill"
          style={{ backgroundColor: "var(--bg-hover)" }}
        >
          <div
            className="h-full rounded-pill"
            style={{
              backgroundColor: "var(--accent)",
              width: `${Math.round(((currentIndex + 1) / steps.length) * 100)}%`,
              transition: `width ${stepDuration * 0.9}ms ease`,
            }}
          />
        </div>

        <p className="mt-2 text-right text-[12px] text-text-muted">
          Step {currentIndex + 1} of {steps.length}
        </p>
      </div>
    </div>,
    document.body
  );
}
